import React from "react";
import {
  Activity,
  AlertCircle,
  BarChart3,
  Eye,
  EyeOff,
  Loader,
  LogIn,
  ShieldCheck,
  Target,
  TrendingUp,
} from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";
import EosBackdrop from "../EosBackdrop";
import { useLoginPage, featureBullets } from "./useLoginPage";
import "./LoginPage.scss";

const LoginPage: React.FC = () => {
  const {
    error,
    isLoading,
    isLoggingIn,
    showPassword,
    formData,
    handleInputChange,
    handleLogin,
    togglePasswordVisibility,
  } = useLoginPage();

  return (
    <div className="eos-dark-shell flex flex-col">
      <EosBackdrop />
      <Header variant="public" />

      <div className="relative z-10 flex-1 lg:grid lg:grid-cols-2">
        <div className="relative overflow-hidden px-4 pt-8 pb-4 sm:px-6 sm:pt-10 lg:flex lg:flex-col lg:justify-center lg:px-12 lg:py-16 xl:px-20">
          <div className="pointer-events-none absolute inset-0 hidden overflow-hidden lg:block">
            <div className="absolute top-16 right-10 text-accent-400 opacity-50 animate-float-slow">
              <BarChart3 className="h-8 w-8 drop-shadow-[0_0_8px_rgba(56,189,248,0.7)]" />
            </div>
            <div className="absolute top-40 right-32 text-emerald-400 opacity-50 animate-float-medium">
              <TrendingUp className="h-8 w-8 drop-shadow-[0_0_8px_rgba(52,211,153,0.7)]" />
            </div>
            <div className="absolute bottom-40 right-16 text-accent-300 opacity-50 animate-float-fast">
              <span className="text-2xl font-bold drop-shadow-[0_0_8px_rgba(125,211,252,0.7)]">₹</span>
            </div>
            <div className="absolute bottom-24 right-48 text-emerald-300 opacity-50 animate-float-medium">
              <Activity className="h-8 w-8 drop-shadow-[0_0_8px_rgba(110,231,183,0.7)]" />
            </div>
            <div className="absolute top-1/2 right-1/3 text-accent-400 opacity-40 animate-pulse-slow">
              <Target className="h-6 w-6" />
            </div>
          </div>

          <div className="trust-badge inline-flex w-fit items-center px-3 py-1 text-sm font-medium text-accent-300">
            <ShieldCheck className="mr-2 h-4 w-4" />
            Trusted by disciplined traders
          </div>

          <h2 className="mt-4 max-w-lg text-2xl font-bold leading-tight sm:mt-6 sm:text-3xl xl:text-4xl">
            <span className="text-slate-100">Trade with confidence, powered by </span>
            <span className="eos-gradient-text">intelligent automation.</span>
          </h2>
          <p className="mt-3 max-w-md text-sm text-slate-400 sm:mt-4 sm:text-base">
            Monitor markets, manage strategies, and execute trades from a single command center built for
            disciplined, data-led trading.
          </p>

          <div className="mt-6 hidden space-y-5 lg:mt-10 lg:block">
            {featureBullets.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="flex items-start gap-4">
                  <div className="feature-bullet-icon flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-accent-300">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-100">{item.title}</p>
                    <p className="mt-0.5 text-sm text-slate-400">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-center px-4 pt-4 pb-8 sm:pt-6 sm:pb-10 lg:py-16">
          <div className="max-w-md w-full animate-fade-up">
            <div className="login-card p-4 sm:p-6 md:p-8">
              <div className="text-center mb-4 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-100 mb-2">Welcome Back</h2>
                <p className="text-slate-400 text-sm sm:text-base">Sign in to access your trading dashboard</p>
              </div>

              {error && (
                <div className="flex items-center justify-center p-3 sm:p-4 mb-4 sm:mb-6 rounded-lg bg-red-500/10 text-red-300 border border-red-500/30">
                  <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 mr-2 flex-shrink-0" />
                  <span className="font-medium text-sm sm:text-base">{error}</span>
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="login-input block w-full rounded-lg px-3 py-2.5 text-base text-white placeholder-slate-500 focus:border-accent-400/60 focus:outline-none focus:ring-2 focus:ring-accent-500/40"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      minLength={6}
                      className="login-input block w-full rounded-lg px-3 py-2.5 pr-10 text-base text-white placeholder-slate-500 focus:border-accent-400/60 focus:outline-none focus:ring-2 focus:ring-accent-500/40"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-accent-300 transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || isLoggingIn}
                  className={
                    isLoading || isLoggingIn
                      ? "w-full flex items-center justify-center px-6 py-3 rounded-full text-base font-semibold bg-white/10 text-slate-400 cursor-not-allowed"
                      : "eos-btn-glow w-full"
                  }
                >
                  {isLoading || isLoggingIn ? (
                    <>
                      <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      <span className="text-sm sm:text-base">Signing In...</span>
                    </>
                  ) : (
                    <>
                      <LogIn className="-ml-1 mr-2 h-4 w-4" />
                      <span className="text-sm sm:text-base">Sign In</span>
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-slate-500">New accounts are currently disabled.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LoginPage;
